import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";
import { formatDuration, getCategoryName, formatViews } from "@/lib/video";

type Props = {
  videos: Video[];
};

export default function HeroCarousel({ videos }: Props) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = videos.length;

  const go = useCallback(
    (next: number) => {
      setIndex((next + count) % count);
    },
    [count],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, 5000);
    return () => clearInterval(timer);
  }, [count]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) {
      go(index + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  if (!count) return null;

  return (
    <section className="px-4 pt-6 md:px-6 md:pt-8">
      <div
        className="relative w-full overflow-hidden rounded-lg bg-foreground-950"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="relative h-[300px] w-full md:h-[480px]">
          {videos.map((video, i) => (
            <Link
              key={video.id}
              to={`/detail/${video.id}`}
              className={`absolute inset-0 block transition-opacity duration-700 ${
                i === index ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
            >
              <img
                src={video.cover}
                alt={`${video.title} - 日批视频焦点视频`}
                title={`${video.title} - 日批视频`}
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-foreground-950/10"></div>
              <div className="absolute inset-x-0 bottom-0 flex flex-col gap-3 p-5 md:p-10">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-label text-background-50">
                    焦点推荐
                  </span>
                  <span className="rounded-full bg-background-50/20 px-3 py-1 text-xs font-label text-background-50 backdrop-blur">
                    {getCategoryName(video.categorySlug)}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-background-100">
                    <i className="ri-time-line text-sm"></i>
                    {formatDuration(video.duration)}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-background-100">
                    <i className="ri-eye-line text-sm"></i>
                    {formatViews(video.views)}
                  </span>
                </div>
                <h2 className="max-w-3xl font-heading text-xl font-extrabold leading-tight text-background-50 md:text-4xl">
                  {video.title}
                </h2>
                <p className="hidden max-w-2xl text-sm leading-relaxed text-background-200 sm:block md:text-base">
                  {video.description}
                </p>
                <span className="mt-1 inline-flex w-fit items-center gap-2 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-label text-background-50 transition-colors duration-200 hover:bg-primary-600">
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </span>
              </div>
            </Link>
          ))}

          <button
            type="button"
            aria-label="上一个"
            onClick={() => go(index - 1)}
            className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/80 text-foreground-900 transition-colors duration-200 hover:bg-background-50 md:flex md:left-6 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            type="button"
            aria-label="下一个"
            onClick={() => go(index + 1)}
            className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/80 text-foreground-900 transition-colors duration-200 hover:bg-background-50 md:flex md:right-6 cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>
        </div>

        <div className="absolute bottom-3 right-5 flex items-center gap-2">
          {videos.map((video, i) => (
            <button
              key={video.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => go(i)}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                i === index ? "w-6 bg-primary-500" : "w-2 bg-background-50/60"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}